/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Select, Text, TextInput } from '@/shared/ui';
import { KNOWN_GUARANTEE_KINDS } from '@/services/requests/housien-services/DealsService';
import { CreateLeaseSubmitButton } from './create-lease/SubmitButton';
import { LeaseService } from '../services/lease-service';
import { leasesKeys } from '../hooks/leases-keys';
import type { Lease } from '../types/lease.types';

export interface LeaseDraftCompletionProps {
  lease: Lease;
  agencyId: string;
}

/**
 * Whether a lease still owes the two answers only a person can give before
 * it can be signed: where rent is settled, and what guarantees it.
 *
 * @param lease - The lease on screen.
 * @returns True for a draft missing either one.
 */
export function needsDraftCompletion(lease: Lease): boolean {
  return lease.state === 'draft' && (!lease.billing_locale || !lease.warranty_decision);
}

/** The one value a single-choice Select hands back. */
function singleValue(raw: string | string[] | null): string {
  if (Array.isArray(raw)) return raw[0] ?? '';
  return raw ?? '';
}

/**
 * Fills in what a draft lacks before "Registrar assinatura" can succeed.
 *
 * A draft opened from a won attendance used to arrive without a billing
 * locale or a guarantee decision, and no screen could set either: the
 * lease sat as a draft forever while activation kept refusing it. This is
 * that screen, shown only while something is missing.
 */
export function LeaseDraftCompletion({ lease, agencyId }: LeaseDraftCompletionProps) {
  const { t } = useTranslation(['features/leases', 'features/deals']);
  const queryClient = useQueryClient();
  const [billingLocale, setBillingLocale] = useState(lease.billing_locale ?? '');
  const [kind, setKind] = useState(lease.warranty_decision?.kind ?? '');
  const [justification, setJustification] = useState(lease.warranty_decision?.justification ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!billingLocale.trim() || !kind) {
      setError(t('draftCompletion.errors.required'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await LeaseService.updateLease(agencyId, lease.id, {
        billing_locale: billingLocale.trim(),
        warranty_decision: { kind, justification: justification.trim() },
      });
      await queryClient.invalidateQueries({ queryKey: leasesKeys.all(agencyId) });
    } catch {
      setError(t('draftCompletion.errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="lease-draft-completion"
      className="flex flex-col gap-(--space-3) rounded-(--radius-lg) border border-(--status-warning-border) bg-(--status-warning-bg) px-(--space-4) py-(--space-4)"
    >
      <div className="flex flex-col gap-(--space-1)">
        <Text
          component="h2"
          className="m-0 text-(length:--text-body) [font-weight:var(--font-semibold)] text-(--text-primary)"
        >
          {t('draftCompletion.title')}
        </Text>
        <Text component="p" className="m-0 text-(length:--text-body-sm) text-(--text-secondary)">
          {t('draftCompletion.description')}
        </Text>
      </div>
      <div className="grid grid-cols-1 gap-(--space-3) md:grid-cols-3">
        <TextInput
          label={t('create.billingLocale')}
          required
          placeholder={t('draftCompletion.billingLocalePlaceholder')}
          value={billingLocale}
          maxLength={100}
          data-testid="lease-draft-billing-locale"
          onChange={(e) => setBillingLocale(e.target.value)}
        />
        <Select
          label={t('draftCompletion.guarantee')}
          required
          name="lease-draft-guarantee"
          data-testid="lease-draft-guarantee"
          value={kind}
          onChange={(raw) => setKind(singleValue(raw))}
          data={KNOWN_GUARANTEE_KINDS.map((value) => ({
            value,
            label: t(`features/deals:terms.guarantees.${value}`),
          }))}
          placeholder={t('draftCompletion.guaranteePlaceholder')}
        />
        <TextInput
          label={t('create.warrantyJustification')}
          placeholder={t('create.warrantyJustificationPlaceholder')}
          value={justification}
          maxLength={2000}
          data-testid="lease-draft-guarantee-note"
          onChange={(e) => setJustification(e.target.value)}
        />
      </div>
      <div className="flex flex-col items-end gap-(--space-2)">
        {error && (
          <Text component="p" role="alert" className="m-0 self-stretch text-(length:--text-meta) text-(--status-error-text)">
            {error}
          </Text>
        )}
        <CreateLeaseSubmitButton
          isSubmitting={saving}
          onSubmit={() => void save()}
          label={t('draftCompletion.submit')}
          savingLabel={t('accrualPeriods.dialog.saving')}
          testId="lease-draft-completion-submit"
        />
      </div>
    </section>
  );
}

export default LeaseDraftCompletion;
